import { defineComponent as _defineComponent } from 'vue'
import { ref, onMounted, nextTick, ComponentPublicInstance } from 'vue';
  import { EntityTypePropertyForm, PropertyDataType } from '@/entities/entityTypeProperty';
  import { ValidationError } from '@/entities/validation';
  import { useModal } from '@/ui/concerns/modals/useModal';
  import { useTask } from '@/ui/concerns';
  import { list as getOptionSets, get as getOptionSet, OptionSetDetail } from '@/services/api/resources/optionSet';
  import { OptionSet, OptionValue } from '@/entities/optionSet';
  import { Versioned } from '@/services/api';
  import PropertyRow from './PropertyRow.vue';
  import DropdownResponseSetModal from './DropdownResponseSetModal.vue';
  import DropdownResponseSetDeleteDialog from './DropdownResponseSetDeleteDialog.vue';
  import { ConfirmationModal } from '@/ui/components/modals/ConfirmationModal';
  import { Button } from '@/ui/components/Button';

  interface Props {
    properties: EntityTypePropertyForm[];
    errors?: ValidationError[];
    disabled?: boolean;
  }

  
export default /*#__PURE__*/_defineComponent({
  __name: 'EntityTypePropertiesSection',
  props: {
    properties: null,
    errors: null,
    disabled: { type: Boolean }
  },
  emits: ['update'],
  setup(__props: any, { emit }) {

const props = __props as {
    properties: EntityTypePropertyForm[];
    errors?: ValidationError[];
    disabled?: boolean;
  };

  

  

  const [showOptionSetModal, openOptionSetModal, closeOptionSetModal] = useModal();
  const [showDeleteConfirm, openDeleteConfirm, closeDeleteConfirm] = useModal();
  const [showDeleteOptionSetDialog, openDeleteOptionSetDialog, closeDeleteOptionSetDialog] = useModal();

  const editingPropertyIndex = ref<number>(-1);
  const editingOptionSet = ref<Versioned<OptionSetDetail> | null>(null);
  const editingOptionSetId = ref<number | null>(null);
  const editingAllowMulti = ref<boolean>(false);
  const deletingOptionSetId = ref<number | null>(null);
  const deletingPropertyId = ref<number | null>(null);
  const optionSets = ref<OptionSet[]>([]);
  const propertyRefs: Record<number, ComponentPublicInstance | Element | null> = {};

  const setPropertyRef = (el: Element | ComponentPublicInstance | null, index: number) => {
    if (el) {
      propertyRefs[index] = el;
    }
  };

  const getOptionSetsTask = useTask(
    () => getOptionSets({ page: 1, perPage: 100 }),
    response => {
      optionSets.value = response.data.map((item: OptionSet) => ({
        id: item.id,
        name: item.name,
        optionValues: item.optionValues.map((ov: OptionValue) => ({
          id: ov.id,
          label: ov.label,
          value: ov.value,
          isActive: ov.isActive,
        })),
        authorizedTo: item.authorizedTo,
      }));
    },
  );

  onMounted(() => {
    getOptionSetsTask.run();
  });

  const updateProperty = (index: number, property: EntityTypePropertyForm) => {
    const updatedProperties = [...props.properties];
    updatedProperties[index] = property;
    emit('update', updatedProperties);
  };

  const addProperty = () => {
    const newProperty: EntityTypePropertyForm = {
      id: undefined,
      label: '',
      dataType: PropertyDataType.Text,
      optionSetId: null,
      allowMulti: false,
    };
    emit('update', [...props.properties, newProperty]);
  };

  const addPropertyAndFocus = () => {
    const currentLength = props.properties.length;
    addProperty();

    nextTick(() => {
      const newPropertyRow = propertyRefs[currentLength];
      const element = (newPropertyRow && '$el' in newPropertyRow) ? newPropertyRow.$el : newPropertyRow;
      element?.querySelector('input')?.focus();
    });
  };

  const confirmDelete = (index: number) => {
    editingPropertyIndex.value = index;
    openDeleteConfirm();
  };

  const deleteProperty = () => {
    const updatedProperties = [...props.properties];
    updatedProperties.splice(editingPropertyIndex.value, 1);
    emit('update', updatedProperties);
    closeDeleteConfirm();
  };

  const fetchOptionSetForEditTask = useTask(
    () => getOptionSet(editingOptionSetId.value as number),
    result => {
      editingOptionSet.value = result;
      openOptionSetModal();
    },
  );

  const openOptionSetModalForEdit = ({ index, optionSetId }: { index: number; optionSetId: number }) => {
    const property = props.properties[index];

    editingAllowMulti.value = property.allowMulti || false;
    editingPropertyIndex.value = index;
    editingOptionSetId.value = optionSetId;

    fetchOptionSetForEditTask.run();
  };

  const onOptionSetSaved = (optionSet: OptionSet, allowMulti: boolean) => {
    const updatedProperties = [...props.properties];
    const property = updatedProperties[editingPropertyIndex.value];

    if (!property) {
      return;
    }

    property.optionSetId = optionSet.id;
    property.allowMulti = allowMulti;
    property.dataType = PropertyDataType.Dropdown;

    // Update the option set in our local list
    const existingIndex = optionSets.value.findIndex((os: OptionSet) => os.id === optionSet.id);
    if (existingIndex >= 0) {
      optionSets.value[existingIndex] = optionSet;
    } else {
      optionSets.value.push(optionSet);
    }

    emit('update', updatedProperties);
  };

  const updateEditingAllowMulti = (value: boolean) => {
    editingAllowMulti.value = value;
  };

  const openCreateOptionSetModal = (index: number) => {
    editingPropertyIndex.value = index;
    editingOptionSet.value = null;
    editingOptionSetId.value = null;
    editingAllowMulti.value = false;
    openOptionSetModal();
  };

  const confirmDeleteOptionSet = (payload: { optionSetId: number; propertyId?: number }) => {
    deletingOptionSetId.value = payload.optionSetId;
    deletingPropertyId.value = payload.propertyId || null;
    openDeleteOptionSetDialog();
  };

  const onOptionSetDeleted = () => {
    // Remove the deleted option set from the local list
    optionSets.value = optionSets.value.filter((os: OptionSet) => os.id !== deletingOptionSetId.value);

    // Update any properties that were using this option set
    const updatedProperties = props.properties.map((prop: EntityTypePropertyForm) => {
      if (prop.optionSetId === deletingOptionSetId.value) {
        return {
          ...prop,
          dataType: PropertyDataType.Text,
          optionSetId: null,
          allowMulti: false,
        };
      }

      return prop;
    });

    emit('update', updatedProperties);
    closeDeleteOptionSetDialog();
  };

return { __sfc: true,props, emit, showOptionSetModal, openOptionSetModal, closeOptionSetModal, showDeleteConfirm, openDeleteConfirm, closeDeleteConfirm, showDeleteOptionSetDialog, openDeleteOptionSetDialog, closeDeleteOptionSetDialog, editingPropertyIndex, editingOptionSet, editingOptionSetId, editingAllowMulti, deletingOptionSetId, deletingPropertyId, optionSets, propertyRefs, setPropertyRef, getOptionSetsTask, updateProperty, addProperty, addPropertyAndFocus, confirmDelete, deleteProperty, fetchOptionSetForEditTask, openOptionSetModalForEdit, onOptionSetSaved, updateEditingAllowMulti, openCreateOptionSetModal, confirmDeleteOptionSet, onOptionSetDeleted, PropertyRow, DropdownResponseSetModal, DropdownResponseSetDeleteDialog, ConfirmationModal, Button }
}

})